import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { AddUserIcon, DeleteIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { friendPlaceEnumMapping } from '../../../../lib/types/enums/friend/friend-place.enum'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { FriendInput } from '../../../../lib/types/models/friend/friend-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import FriendForm from './friend-form'

const FriendsEnrollmentForm = () => {
  const form = useFormContext<EnrollmentInput>()
  const [index, setIndex] = useState<number>()
  const { t } = useTranslate()

  const declaredFriends = form.watch('declaredFriends')

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    FriendInput
  >(['create', 'edit', 'delete'])

  const columns: Column<IdInput<FriendInput>>[] = [
    {
      enableSorting: true,
      header: 'NOM',
      accessorKey: 'lastName'
    },
    {
      enableSorting: true,
      header: 'Prénom',
      accessorKey: 'firstName'
    },
    {
      enableSorting: true,
      header: 'Demande',
      accessorKey: 'type',
      cell: ({ row }) => t(friendPlaceEnumMapping[row.original.type].label)
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              setIndex(row.index)
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              setIndex(row.index)
              toggleModal('delete')
            }}
          />
        </div>
      )
    }
  ]

  return (
    <InnerBox title="Séjourner avec un ami" className="rounded-2xl">
      <div className="flex flex-col gap-2 items-start">
        <Typography variant="label-semibold">
          Votre enfant souhaite-t-il partir avec un ami ?
        </Typography>
        <Button
          type="button"
          variant="secondary"
          label="Ajouter un ami"
          icon={PlusIcon}
          onClick={() => toggleModal('create')}
        />
        {(declaredFriends ?? []).length > 0 && (
          <div className="w-full">
            <DataTable<IdInput<FriendInput>>
              manualSorting={false}
              columns={columns}
              data={declaredFriends?.map((friend, index) => ({ ...friend, id: index })) ?? []}
              rowCount={declaredFriends?.length ?? 0}
              paginated={false}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un ami du participant'}
        size="medium"
        hideActions
        icon={() => <AddUserIcon color={tailwindColors.secondary.DEFAULT} />}
        onCancel={() => closeModel('create')}
      >
        <FriendForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (value) => {
              form.setValue('declaredFriends', [...(declaredFriends ?? []), value])
              closeModel('create')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.edit}
        title={toFullName(selectedData?.firstName, selectedData?.lastName)}
        hideActions
        icon={() => <AddUserIcon color={tailwindColors.secondary.DEFAULT} />}
        size="medium"
        onCancel={() => closeModel('edit')}
      >
        <FriendForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: (values) => {
              form.setValue(
                'declaredFriends',
                declaredFriends?.map((friend, i) => (i === index ? values : friend))
              )
              closeModel('edit')
            }
          })}
        />
      </Modal>

      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            form.setValue(
              'declaredFriends',
              declaredFriends?.filter((_, i) => i !== index)
            )
            closeModel('delete')
          }
        }}
        onCancel={() => toggleModal('delete')}
        isOpen={modalState.delete}
        title={'Êtes-vous sûr de vouloir supprimer les informations de ce participant ?'}
        icon={DeleteIcon}
        variant={'destructive'}
      />
    </InnerBox>
  )
}

export default FriendsEnrollmentForm
